import { useState } from 'react';
import { Helmet } from "react-helmet";
import {
  MapPinIcon,
  EnvelopeIcon,
  FunnelIcon,
  PlayCircleIcon,
  DocumentTextIcon,

} from "@heroicons/react/24/outline";

// components
import { SectionHeader } from "../../components/layouts/section.header";
import { SectionSubHeader } from "../../components/layouts/section.subheader";
import { TableSupportedLocations } from "../../tables/supported.locations";
import { ModalAddLocation } from "../../components/modals/modal.add.location";
import { TableArticles } from "../../tables/articles";
import { ModalAddArticle } from "../../components/modals/modal.add.article";
import { TableMediaGuides } from "../../tables/media.guides";
import { ModalAddMediaGuide } from "../../components/modals/modal.add.media.guide";

// utilities
import { classNames } from "../../utilities";

/**
 * Malaysian states for the dropdown filter.
 * Matches what the backend expects in the `state` query parameter.
 */
const MALAYSIAN_STATES = [
  'Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan',
  'Pahang', 'Penang', 'Perak', 'Perlis', 'Sabah',
  'Sarawak', 'Selangor', 'Terengganu', 'Wilayah Persekutuan Kuala Lumpur', 'Putrajaya', 'Labuan'
];

/**
 * Tab definitions for the Settings sub-sections.
 * Each has a key, label, and icon — making it easy to add more tabs later.
 */
const TABS = [
  { key: 'locations', label: 'Supported Locations', icon: MapPinIcon },
  { key: 'articles', label: 'Articles', icon: DocumentTextIcon },
  {key: 'media guide', label : 'Media Guide', icon: PlayCircleIcon }
];

export const ScreenSettingsLocations = () => {

  // ===== STATE =====
  const [activeTab, setActiveTab] = useState('locations'); // which tab is active
  const [selectedState, setSelectedState] = useState('');  // state filter for locations
  const [showAddModal, setShowAddModal] = useState(false);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [articleActiveFilter, setArticleActiveFilter] = useState('');
  const [editingArticle, setEditingArticle] = useState(null);
  const [showMediaGuideModal, setShowMediaGuideModal] = useState(false);
  const [mediaGuideActiveFilter, setMediaGuideActiveFilter] = useState('');
  const [editingMediaGuide, setEditingMediaGuide] = useState(null);

  return (
    <>
      <Helmet>
        <title>Settings</title>
      </Helmet>
      <div className="h-screen w-full overflow-y-auto pb-20">

        {/* PAGE HEADER — same pattern as Dashboard "Mission Board" */}
        <SectionHeader title='Settings' />

        {/* ========================== TAB NAVIGATION =============================== */}
        {/*
          This is a simple horizontal tab bar. We use the same colour tokens
          (primary-600, primary-700) that the sidebar uses so it feels cohesive.
          The active tab gets an underline + bold colour.
        */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Settings tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={classNames(
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                  'group inline-flex items-center border-b-2 py-3 px-1 text-sm font-medium'
                )}
              >
                <tab.icon
                  className={classNames(
                    activeTab === tab.key ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500',
                    '-ml-0.5 mr-2 h-5 w-5'
                  )}
                />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* ===== TAB CONTENT ===== */}
        <div className="mt-8">

          {/* ---------------------------- SUPPORTED LOCATIONS TAB -------------------------- */}
          {activeTab === 'locations' && (
            <div>
              {/* Sub-header with icon */}
              <div className="w-full flex flex-row justify-between items-center">
                <SectionSubHeader Icon={MapPinIcon} title='Supported Locations' />
              </div>

              {/*
                FILTER BAR
                Dropdown to filter by state. When "All States" is selected (empty string),
                the table loads everything. When a specific state is picked, it narrows down.
              */}
              <div className="mt-6 flex flex-wrap items-center gap-4 rounded-lg bg-gray-50 p-4 border border-gray-200">
                <div className="flex items-center gap-2">
                  <FunnelIcon className="h-5 w-5 text-gray-400" />
                  <label htmlFor="state-filter" className="text-sm font-medium text-gray-700">
                    Filter by State
                  </label>
                </div>
                <select
                  id="state-filter"
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All States</option>
                  {MALAYSIAN_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>

                {selectedState && (
                  <span className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 ring-1 ring-inset ring-primary-600/20">
                    Filtered: {selectedState}
                  </span>
                )}

                {/* Add new button — at the right side of filter bar */}
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="ml-auto inline-flex items-center px-3 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                  >
                    + Add New Location
                  </button>
              </div>

              {/* 
                TABLE — always rendered.
                When selectedState is '' (All States), the table loads ALL locations 
                via the country-index GSI and groups them by state.
                When a specific state is picked, it queries only that state.
              */}
              <TableSupportedLocations selectedState={selectedState} />
            </div>
          )}

          {/* --------------------------- ARTICLES TAB ------------------------------ */}
          {activeTab === 'articles' && (
            <div>
              <div className="w-full flex flex-row justify-between items-center">
                <SectionSubHeader Icon={DocumentTextIcon} title='Articles' />
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4 rounded-lg bg-gray-50 p-4 border border-gray-200">
                <div className="flex items-center gap-2">
                  <FunnelIcon className="h-5 w-5 text-gray-400" />
                  <label htmlFor="article-active-filter" className="text-sm font-medium text-gray-700">
                    Filter by Active
                  </label>
                </div>
                <select
                  id="article-active-filter"
                  value={articleActiveFilter}
                  onChange={(e) => setArticleActiveFilter(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>

                {articleActiveFilter && (
                  <span className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 ring-1 ring-inset ring-primary-600/20">
                    Filtered: {articleActiveFilter === 'active' ? 'Active' : 'Inactive'}
                  </span>
                )}

                <button
                  onClick={() => { setEditingArticle(null); setShowArticleModal(true); }}
                  className="ml-auto inline-flex items-center px-3 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                >
                  + Add New Article
                </button>
              </div>

              <TableArticles
                activeFilter={articleActiveFilter}
                onEdit={(article) => { setEditingArticle(article); setShowArticleModal(true); }}
              />
            </div>
          )}

          {/* --------------------------- MEDIA GUIDE TAB ------------------------------- */}
          {activeTab === 'media guide' && (
            <div>
              <div className="w-full flex flex-row justify-between items-center">
                <SectionSubHeader Icon={PlayCircleIcon} title='Media Guide' />
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4 rounded-lg bg-gray-50 p-4 border border-gray-200">
                <div className="flex items-center gap-2">
                  <FunnelIcon className="h-5 w-5 text-gray-400" />
                  <label htmlFor="mediaguide-active-filter" className="text-sm font-medium text-gray-700">
                    Filter by Active
                  </label>
                </div>
                <select
                  id="mediaguide-active-filter"
                  value={mediaGuideActiveFilter}
                  onChange={(e) => setMediaGuideActiveFilter(e.target.value)}
                  className="rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>

                {mediaGuideActiveFilter && (
                  <span className="inline-flex items-center rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 ring-1 ring-inset ring-primary-600/20">
                    Filtered: {mediaGuideActiveFilter === 'active' ? 'Active' : 'Inactive'}
                  </span>
                )}

                <button
                  onClick={() => { setEditingMediaGuide(null); setShowMediaGuideModal(true); }}
                  className="ml-auto inline-flex items-center px-3 py-2 rounded-md bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                >
                  + Add New Media Guide
                </button>
              </div>

              <TableMediaGuides
                activeFilter={mediaGuideActiveFilter}
                onEdit={(mediaGuide) => { setEditingMediaGuide(mediaGuide); setShowMediaGuideModal(true); }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Add Location Modal */}
      <ModalAddLocation
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />
      <ModalAddArticle
        isOpen={showArticleModal}
        onClose={() => setShowArticleModal(false)}
        article={editingArticle}
      />
      <ModalAddMediaGuide
        isOpen={showMediaGuideModal}
        onClose={() => setShowMediaGuideModal(false)}
        mediaGuide={editingMediaGuide}
      />
    </>
  );
};